<script>
   /*************************************************************/
   /* PrepCreator                                               */
   /* ------------                                              */
   /* creates preprocessing object with directives and user     */
   /* defined settings and apply it to short data               */
   /*************************************************************/

   import { tick } from 'svelte';
   import { Container, ButtonAdd, ButtonSettings, Button } from 'svelte-controls-basic';
   import { prep_fit, prep_apply, methodsList } from '@mdatools-apps/wasm/prep.js';
   import { cloneShortData, getShortData, NROWS, MAX_NMETHODS } from '../../methods/prep.js';

   import PrepNorm from './widgets/PrepNorm.svelte';
   import PrepSavgol from './widgets/PrepSavgol.svelte';
   import PrepScale from './widgets/PrepScale.svelte';
   import PrepBaseline from './widgets/PrepBaseline.svelte';
   import PrepTrim from './widgets/PrepTrim.svelte';
   import PrepSpikes from './widgets/PrepSpikes.svelte';

   let {
      WASM,                            // WASM module
      rawdata,                         // original data loaded by user
      prepobj = $bindable([]),         // JSON with preprocessing object
      meta = $bindable(),              // Array with meta information (text settings values)
      varind = $bindable([-1, -1, -1]), // marker indices: normalization variable, trim bounds
      rawdataShort = $bindable(),
      prepdataShort = $bindable(),
      prepmodel = null,                // just to trace if model is available or not use it only in prep app
      left = $bindable(0),             // trim bounds of the whole chain, the parent reads them too
      right = $bindable(0),
      reset = null,
      finalize = null,
   } = $props();

   // constant parameters
   const methodsComponents = {      // list of available methods components
      'savgol' : PrepSavgol,
      'norm': PrepNorm,
      'scale': PrepScale,
      'baseline': PrepBaseline,
      'trim': PrepTrim,
      'spikes': PrepSpikes,
   };

   // error message for WASM actions
   let errMsg = $state('');

   // generation counter for the preview runs - only the newest may write. see the longer note in
   // 'PrepWidget'; plain 'let', never '$state' and never module scope
   let shortRun = 0;

   // state variables for business logic
   let openMethod = $state(null);       // position of the open settings panel, null for none
   let showCancel = $state(false);


   // state variables to keep left and right values for each item in prepobj
   let leftVec = $state([]);
   let rightVec = $state([]);

   // prepare meta as empty array (only if not already set by parent)
   if (!Array.isArray(meta) || meta.length !== MAX_NMETHODS) {
      meta = new Array(MAX_NMETHODS).fill('');
   }

   // derived for keeping nvars
   const nvars = $derived(rawdata.values.ncols - rawdata.nresp);

   // local copy of the available methods list: the ids the wrapper knows, in the map's order.
   // the menu renders each entry by its wrapper record, so an id the wrapper lacks crashed it
   const methodsListLocal = $derived(
      Object.keys(methodsComponents)
         .filter(id => methodsList.some(v => v.id === id))
         .filter(id => id !== 'scale' || rawdata.values.nrows >= 3)
   );

   // when component is being reloaded make sure trimming is correct
   trimTails();

   // if prepobject is not empty (e.g. user edit already created preprocessing routine)
   // apply it to short data
   if (prepobj.length !== 0) prepApplyShort();


   /****************************/
   /* Helper functions         */
   /****************************/

   // applies current preprocessing sequence to short dataset.
   async function prepApplyShort() {

      const myRun = ++shortRun;
      errMsg = '';

      if (prepobj.length === 0) return;

      // a widget corrects its parameters in an effect, a filter width or a baseline degree pulled
      // down to the interval a changed trim leaves, and effects run after this call: a chain read
      // here was the one before the correction, so the preview failed on a width the chain no
      // longer held. the tick lets the corrections settle first
      await tick();
      if (myRun !== shortRun) return;

      // 'prepobj' is a deeply reactive array which the callback below would otherwise read live,
      // so a chain edited while the preview is in flight would be the chain that gets previewed.
      // the snapshot makes the run a real generation instead of a reference to whatever the chain
      // becomes by the time the module is ready
      const chain = $state.snapshot(prepobj);

      WASM()
         .then(Module => {
            if (myRun !== shortRun) return;

            // 'rawdataShort' is filled by an effect and can still be empty when a preview for an
            // already existing chain starts, so it is read here, once, rather than at call time
            const data = rawdataShort;
            if (!data) return;

            // fit model and add extra parameters
            const m = prep_fit(Module, data.values, 0, chain, true, []);
            const v = prep_apply(Module, data.values, 0, m, true);

            // hand the preview back to the parent as a copy carrying the preprocessed values
            prepdataShort = cloneShortData(data, v);
         })
         .catch(e =>{
            console.error(e);
            if (myRun !== shortRun) return;
            prepdataShort = cloneShortData(rawdataShort);
            errMsg = e instanceof Error ? e.message : String(e);
         });
   }

   // the pair is set together: a left tested against the right still in place refused a trim
   // moved past it, and the preview kept the old left
   function setTrim(l, r) {
      if (l >= 0 && l < r && r <= nvars) {
         left = l;
         right = r;
      }
   }

   // Checks "prepobj" elements if it contains trimming, adjust left and right values accordingly.
   function trimTails() {
      const npred = rawdata.values.ncols - rawdata.nresp;
      if (!prepobj || prepobj.length < 1) {
         setTrim(0, npred);
         leftVec = [];
         rightVec = [];
         return
      }

      let lv = [];
      let rv = [];
      let l = 0;
      let r = npred;
      for (let v of prepobj) {
         if (v.id === 'trim') {
            lv.push(l);
            rv.push(r);
            // correct params if they are beyond the borders (e.g. after moving). a trim the
            // interval holds none of, which an earlier trim edited past it leaves, is voided to
            // the interval: clipped at one end only, the pair was crossed, and the trim of the
            // data threw on it
            if (v.params[0] < l) v.params[0] = l;
            if (v.params[1] > r) v.params[1] = r;
            if (v.params[1] <= v.params[0]) { v.params[0] = l; v.params[1] = r; }
            l = v.params[0] > l ? v.params[0] : l;
            r = v.params[1] > 0 && v.params[1] < r ? v.params[1] : r;
         } else {
            lv.push(l);
            rv.push(r);
         }
      }
      leftVec = lv;
      rightVec = rv;
      setTrim(l, r);
   }

   // adds method with provided "id" to selected list.
   function add(id) {
      // locate the method by id
      const method = methodsList.find(v => v.id === id);
      if (!method) return

      // no add or open while a panel is open (Tab reaches their buttons past its overlay): Cancel
      // then closed the panel without Save, so its edits were never applied
      if (openMethod !== null) return;

      // a panel edits the chain at once, so a model fitted to it before is dropped
      if (reset) reset();

      // add the method with its defaults to the chain (the available list is derived and stays)
      leftVec.push(left);
      rightVec.push(right);
      // copy the defaults rather than aliasing 'method.mp' from 'methodsList'. no write reaches it
      // today - Svelte's state proxy mutates its own signal map, never the target array - so this
      // is hygiene: the alias made that a property of the framework version, not of this file
      prepobj.push({id: method.id, params: [...method.mp]});

      // open the settings dialog
      showCancel = true;
      openMethod = prepobj.length - 1;
   }

   // cancels adding the last method.
   function cancel() {

      if (prepobj.length < 1) return;

      leftVec.pop();
      rightVec.pop();
      prepobj.pop();
      showCancel = false;
      openMethod = null;

      // the popped entry's widget is destroyed with its panel open, so the effect which clears
      // the slots it wrote never runs its closing branch; the marker stayed on the plot
      varind[0] = -1;
      varind[1] = -1;
      varind[2] = -1;
   }


   // moves method at position "pos" one step up
   function moveUp(pos) {

      if (pos <= 0) return;

      // swap the method with the one above
      const method = prepobj[pos];
      const prev = prepobj[pos - 1];
      prepobj[pos - 1] = method;
      prepobj[pos] = prev;
      apply();
   }

   // moves method at position "pos" one step down
   function moveDown(pos) {

      // find position of the method in the list
      if (pos < 0 || pos === prepobj.length - 1) return;

      // swap the method with the one below
      const method = prepobj[pos];
      const next = prepobj[pos + 1];
      prepobj[pos + 1] = method;
      prepobj[pos] = next;
      apply();
   }

   // removes method at position "pos" from the selected list.
   function remove(pos) {

      // find the method in the list
      if (pos < 0 || pos >= prepobj.length) return;

      // remove the method from the chain: the available list is derived, nothing goes back to it
      prepobj = prepobj.filter((v, i) => i !== pos);
      apply();
   }

   // opens settings dialog for method at position "pos" in the method list
   function open(pos) {
      // see 'add'
      if (openMethod !== null) return;
      if (reset) reset();
      openMethod = pos;
   }

   // applies preprocessing to the short dataset.
   function apply() {
      errMsg = '';
      showCancel = false;
      openMethod = null;

      // every committed edit invalidates a full model, including one whose fit is still in flight.
      // testing 'prepmodel' here would miss exactly that case, since a pending fit has not landed
      // yet. only the standalone prep app passes 'reset', and before finalize the fields it clears
      // are already null, so the extra call changes nothing there
      if (reset) reset();

      // preprocessing chain is about to change - reset model and preprocessed data
      trimTails();
      prepApplyShort();
      varind[1] = -1;
      varind[2] = -1;
   }


   /****************************/
   /* Effects                  */
   /****************************/

   // the short set is cut when it is missing and the preprocessed one starts as its copy. one
   // effect: the copy follows the cut in the same run, not in a second effect woken by the first
   $effect(() => {
      if (rawdata && !rawdataShort) {
         rawdataShort = getShortData(rawdata, NROWS);
      }
      if (rawdataShort && !prepdataShort) {
         prepdataShort = cloneShortData(rawdataShort);
      }
   })

   // effect to reset all component settings if there are no methods in preprocessing object. the
   // preview is written as a fresh copy of the short set here: a null would unmount the plots a
   // parent gates on the preview, and the effect above would write the same copy in a second run
   $effect(() => {
      if (!prepobj || prepobj.length === 0) {
         if (rawdataShort) prepdataShort = cloneShortData(rawdataShort);
         leftVec = [];
         rightVec = [];
         openMethod = null;
         meta = new Array(MAX_NMETHODS).fill('');
      }
   });

   // in the standalone prep app the preview fields are bound to state in the tab container, so
   // they outlive this component and a run still in flight would write a preview into a chain
   // the user has left
   $effect(() => () => { shortRun++; });
</script>

<div class="selected-methods-list"  class:final={prepmodel}>

   <!-- block header with edit button -->
   <Container label="">
      <h2>Selected methods</h2>
      {#if prepmodel && reset}
      <ButtonSettings title="Edit" onclick={reset}/>
      {/if}
   </Container>

   {#if prepobj.length === 0}
      <Container label=""><p>No methods selected</p></Container>
   {:else}
      <!-- keyed on the entry: the baseline widget seeds its penalty range from a parameter at
           mount and writes the parameter back in an effect, so an instance reused by index after
           a move or a removal wrote its method's settings over the entry that took its place -->
      {#each prepobj as m, i (m)}
         {@const Component = methodsComponents[m.id]}
         {#if m.id === 'norm' || m.id === 'trim'}
         <Component
            bind:params={m.params}
            bind:varind={varind}
            bind:settings={meta[i]}
            isOpen={i === openMethod}
            left={leftVec[i]}
            right={rightVec[i]}
            pos={i}
            {nvars}
            {moveUp} {moveDown} {apply} {open} {remove} cancel={showCancel ? cancel : null}
         />
         {:else}
         <Component
            bind:params={m.params}
            bind:settings={meta[i]}
            pos={i}
            isOpen={i === openMethod}
            left={leftVec[i]}
            right={rightVec[i]}
            {nvars}
            {moveUp} {moveDown} {apply} {open} {remove} cancel={showCancel ? cancel : null}
         />
         {/if}
      {/each}
   {/if}

</div>

<!-- error message, outside the list: as its last child it took the last method's place in the
     rule below, and that method's down button showed while the message did -->
{#if errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}

<!-- the row is the only child of this div, so the library's first-child and last-child rules
     round its corners and set its margins; between the two lists it would be neither -->
<div>
   {#if !prepmodel && openMethod === null && prepobj.length > 0}
      <Container label=" ">
         <Button onclick={finalize} text="Finalize" />
      </Container>
   {/if}
</div>

<div class="methods-list">
   <Container label=""><h2>Available methods</h2></Container>
   {#if prepobj.length >= MAX_NMETHODS}
      <Container label=""><p>Maximum number of methods is selected.</p></Container>
   {:else}
      {#each methodsListLocal as id}
      {@const m = methodsList.find(v => v.id === id)}
      <Container>
         <p>{m.title}</p>
         <ButtonAdd onclick={() => add(m.id)} />
      </Container>
      {/each}
   {/if}
</div>




<style>
   .selected-methods-list {
      background: #fefafaa0;
      border-radius: 5px;
   }

   /* the row's own spacing of its buttons: scoped, so it wins over the library's margins by
      specificity and not by the order the two stylesheets land in */
   .selected-methods-list > :global(.control-element .button-up) {
      box-sizing: border-box;
      margin: 0;
   }

   .selected-methods-list > :global(.control-element .button-down) {
      box-sizing: border-box;
      margin: 0;
      margin-right: 0.3em;
   }

   .selected-methods-list > :global(.control-element:last-child .button-down),
   .selected-methods-list > :global(.control-element:nth-child(2) .button-up) {
      cursor:text;
      background-color: transparent;
      color: transparent;
   }


   .selected-methods-list > :global(.control-element .button-settings) {
      display: block;
   }

   .methods-list {
      margin-top: 1em;
      font-size: 0.9em;
   }

   .methods-list > :global(div) {
      background: #ebf0f0;
   }

   .final :global(.button-up),
   .final :global(.button-down),
   .final :global(.button-cancel) {
      visibility: hidden;
   }

</style>